import React, { useContext } from "react";
import { useTranslation } from "utilities/i18n";
import { Link } from "polyrouter";
import {
  Category,
  EpisodeQueryFilter,
  InsightsPriorityQueryVariables,
  LocationScope,
  Topic,
  useInsightsCategoryQuery,
  useTopicsQuery
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import {
  FeatureFlagsOrEnabled,
  LoadingIndicator,
  PolyIcon,
  PremiumBetaBadge
} from "elements";
import { getInsightTitleTranslation } from "utilities/insights";
import { findRelativeTimePeriod, tMetric } from "collections/InsightDetails";
import { Card } from "collections/Cards";
import { mapInsight } from "routes/Insights/Priority/usePriorityInsight";
import { usePriorityFilter } from "routes/Insights/Priority/QueryParams";
import { InsightsCategorySwitcher } from "routes/Insights/Priority/PriorityInsights";
import { generatePath } from "react-router";
import { trackInterest } from "monitoring";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

type InsightCategory = Pick<
  Topic,
  "id" | "validLocationScopes" | "name" | "category"
>;

const CategoryCard: React.FC<{ category: InsightCategory }> = ({
  category
}) => {
  const { t } = useTranslation();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const variables: InsightsPriorityQueryVariables = {
    tenant: [tenantId!],
    first: 1,
    input: {
      topic: [category.id],
      threshold: 0.125,
      episodeActivity: EpisodeQueryFilter.Active
    }
  };
  const tM = tMetric();
  const { data, fetching } = useInsightsCategoryQuery({ variables });

  if (fetching) {
    return (
      <Card>
        <LoadingIndicator />
      </Card>
    );
  }

  const topicIndicator = data?.topicIndicators.edges[0];
  const insight = topicIndicator ? mapInsight(topicIndicator) : null;
  const description = findRelativeTimePeriod(
    insight?.timePeriodStart || "",
    insight?.period
  );
  const metric = tM(insight?.metric);
  const id = insight?.id ?? "";

  const card = (
    <Card>
      <h4 data-testid={category.category} className="header mt-5">
        {category.name} {id && <PolyIcon icon="keyboard_arrow_right" />}
      </h4>
      <span className="metric">{metric}</span>
      <p className="description">
        {description ? description : t("NO_INSIGHTS_AVAILABLE")}
      </p>
      {category.category === Category.Room && <PremiumBetaBadge />}
    </Card>
  );

  if (!id) {
    return <div data-testid={category.id}>{card}</div>;
  }

  const to = {
    pathname: generatePath("/insights/details/:id", { id }),
    search: "r=categories"
  };

  return (
    <Link
      to={to}
      persist={["InsightsFilterState"]}
      onClick={trackInterest(
        insight?.title ?? "",
        "InsightsCategories",
        insight?.locationScope,
        insight?.period
      )}
      data-testid={category.id}
    >
      {card}
    </Link>
  );
};

const InsightCategoriesLoader: React.FC = () => (
  <>
    <FeatureFlagsOrEnabled
      feature="INSIGHTS_ROOM_CATEGORIES"
      orFeature="IS_POLY_USER"
      elseRender={<InsightCategories />}
    >
      <InsightCategories premium />
    </FeatureFlagsOrEnabled>
  </>
);

const InsightCategories: React.FC<{ premium?: boolean }> = ({
  premium = false
}) => {
  const { t } = useTranslation();
  const { data, fetching } = useTopicsQuery();
  const { Content } = Layout;
  let devices = [] as Array<InsightCategory>;
  let rooms = [] as Array<InsightCategory>;

  if (data && data.topics) {
    data.topics
      .map(topic => ({
        ...topic,
        name: getInsightTitleTranslation(topic.id)
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(topic => {
        if (topic.validLocationScopes.includes(LocationScope.Device)) {
          devices.push(topic);
        } else if (
          premium &&
          topic.validLocationScopes.includes(LocationScope.Room)
        ) {
          rooms.push(topic);
        }
      });
  }

  const [filterState, setFilter] = usePriorityFilter();

  return (
    <Layout>
      <Content scrolling>
        {premium && (
          <div className="switcher-container text-center">
            <InsightsCategorySwitcher
              filterState={filterState}
              setFilter={setFilter}
              className="ml-auto"
            />
          </div>
        )}
        <Content.ScrollingList loading={fetching}>
          <div className="categories">
            {premium && !filterState.category && (
              <h3 className="mt-0">{t("DEVICES")}</h3>
            )}
            <div className="device">
              {(!premium || filterState.category !== Category.Room) &&
                devices.map((topic, idx) => (
                  <CategoryCard category={topic} key={topic.id + ":" + idx} />
                ))}
            </div>
            <div>
              {premium && !filterState.category && (
                <h3 className="mt-15">{t("ROOMS")}</h3>
              )}
              <div className="rooms">
                {premium &&
                  filterState.category !== Category.Device &&
                  rooms.map((topic, idx) => (
                    <CategoryCard category={topic} key={topic.id + ":" + idx} />
                  ))}
              </div>
            </div>
          </div>
        </Content.ScrollingList>
      </Content>
    </Layout>
  );
};

export default InsightCategoriesLoader;
